import HttpRepository from './httpRepository';

import type { CodingList } from '../queries/codingQuery';
import type { AxiosError, AxiosResponse } from 'axios';

const CodingRepository = {
	DEFAULT_PROVIDER_MAPPING: {
		coding1: '',
		coding2: '',
		coding3: '',
		coding4: '',
		coding5: '',
		coding6: '',
		coding7: '',
		coding8: '',
	},

	getCodingLists: async ({ skipDisabled }: { skipDisabled: boolean }): Promise<any> => {
		const response: AxiosResponse<CodingList[], AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/rest/coding/lists?includeDisabled=${skipDisabled ? 'false' : 'true'}`,
		);
		return response.data;
	},

	saveCodingList: async ({ codingList }: { codingList: any }): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().post('/rest/coding/lists/', {
			code: codingList.code,
			description: codingList.description,
			group: codingList.group,
			id: codingList.id,
			name: codingList.name,
			ordering: codingList.ordering,
			target: codingList.target,
		});
		return response.data;
	},

	deleteCodingList: async ({ selectedCodingList }: { selectedCodingList: any }): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().delete(`/rest/coding/lists/${selectedCodingList.code}`);
		return response.data;
	},

	getCodingListEntries: async ({ codingListCode }: { codingListCode: string }): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().get(`/rest/coding/lists/${codingListCode}/entries`);
		return response.data;
	},

	getInactiveCodingListEntries: async ({ companyId }: { companyId?: string }): Promise<any> => {
		if (companyId) {
			const response = await HttpRepository.tradeshiftGo().get(
				`/rest/company/${companyId}/coding/lists/inactiveEntries`,
			);
			return response.data;
		} else {
			return {};
		}
	},

	deleteCodingListEntry: async ({
		codingListCode,
		codingEntryId,
	}: {
		codingListCode: string;
		codingEntryId: string;
	}): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().delete(
			`/rest/coding/lists/${codingListCode}/entries/${codingEntryId}`,
		);
		return response.data;
	},

	saveCodingListEntry: async ({
		codingListCode,
		codingEntryId,
		codingEntryLabel,
	}: {
		codingListCode: string;
		codingEntryId: string;
		codingEntryLabel: string;
	}): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().put(
			`/rest/coding/lists/${codingListCode}/entries/${codingEntryId}`,
			{
				entryLabel: codingEntryLabel,
			},
		);
		return response.data;
	},

	changeCodingListEntryId: async ({
		codingListCode,
		codingEntryId,
		newCodingEntryId,
		codingEntryLabel,
	}: {
		codingListCode: string;
		codingEntryId: string;
		newCodingEntryId: string;
		codingEntryLabel: string;
	}): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().put(
			`/rest/coding/lists/${codingListCode}/entries/${newCodingEntryId}?replaceEntryId=${codingEntryId}`,
			{
				entryLabel: codingEntryLabel,
			},
		);
		return response.data;
	},

	enableCodingList: async ({ codingListCode }: { codingListCode: string }): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().post(`/rest/coding/lists/${codingListCode}/enable`);
		return response.data;
	},

	disableCodingList: async ({ codingListCode }: { codingListCode: string }): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().post(`/rest/coding/lists/${codingListCode}/disable`);
		return response.data;
	},

	toggleCodingListEntry: async ({
		codingListCode,
		codingEntryId,
		teamId,
		state,
	}: {
		codingListCode: string;
		codingEntryId: string;
		teamId: string;
		state: boolean;
	}): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().put(
			`/rest/teams/${teamId}/coding/lists/${codingListCode}/entries/${codingEntryId}/state`,
			{
				entryId: codingEntryId,
				value: state,
			},
		);
		return response.data;
	},

	deleteProviderMapping: async (): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().delete('/rest/configuration/ProviderApiMap');
		return response.data;
	},

	setProviderMapping: async (value: any): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().post('/rest/configuration/ProviderApiMap', { value });
		return response.data;
	},

	getProviderMapping: async (): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().get('/rest/configuration/ProviderApiMap');
		return response.data;
	},

	getProviderFields: async (): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().get('/rest/coding/providerFields');
		return response.data;
	},

	saveProviderFields: async ({ providerFields }: { providerFields: any }): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().put('/rest/coding/providerFields', providerFields);
		return response.data;
	},

	getTeamCodingListEntries: async ({ teamId, listId }: { teamId: string; listId: string }) => {
		const response: AxiosResponse<CodingList[], AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/coding/lists/${listId}/entries`,
		);
		return response.data;
	},

	setCodingEntryState: async ({
		entryId,
		entryState,
		listId,
		teamId,
	}: {
		entryId: string;
		entryState: any;
		listId: string;
		teamId: string;
	}): Promise<any> => {
		const response = await HttpRepository.tradeshiftGo().put(
			`/rest/teams/${teamId}/coding/lists/${listId}/entries/${entryId}/state`,
			entryState,
		);
		if (response.status === 204) {
			return {};
		} else if (response.status >= 200 && response.status < 300) {
			return response.data;
		} else {
			throw new Error('An error occurred while setting the coding entry state.');
		}
	},
};

export default CodingRepository;
